import { Box, Grid, Input, Stack, Text, Tooltip } from '@chakra-ui/react';
import { LockIcon } from '@chakra-ui/icons';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { ContractAgreementCheckbox } from '../components/ContractAgreementCheckbox';
import { SavedCardExpirationAlert } from '../components/SavedCardExpirationAlert';
import { APPLE_PAY_METHOD, CheckoutFormProps, CheckoutSuccess, GOOGLE_PAY_METHOD, useCheckoutForm, WALLET_METHODS } from '../useCheckoutForm';
import { Advantages } from './Advantages';
import { CardBrandsRow } from './CardBrands';
import { AlmaPlans } from './AlmaPlans';
import { CardFields } from './CardFields';
import { Confirmation } from './Confirmation';
import { formatAmount } from './format';
import { OrderSummary } from './OrderSummary';
import { PaymentOption } from './PaymentOption';
import { WalletExpress, WalletId } from 'components/WalletExpress';
import { WalletPaymentOverlay } from 'components/WalletPaymentOverlay';
import { CheckoutLayoutContext } from './layout';
import { Field, Section } from './Section';
import { PageHeader } from './Stepper';
import { configuredFont, cssVars } from './tokens';
import { useContainerWidth } from './useContainerWidth';
import './checkout-v2.css';

const FORM_ID = 'checkout-v2-form';

type OptionId = 'saved' | 'new' | WalletId | `custom:${string}`;

/**
 * Nouveau Panier — rendu premium du récapitulatif de commande, panier de la
 * nouvelle interface (cf. flag.ts). Toute la logique vient de useCheckoutForm ;
 * ce composant n'est qu'une autre peau sur le même état : mêmes champs, mêmes
 * validations, mêmes appels serveur que le rendu historique.
 *
 * Conteneur large : formulaire à gauche, récapitulatif sombre collé à droite,
 * bouton en bas du formulaire. Conteneur étroit / mobile : récapitulatif
 * replié en haut, barre de paiement fixe en bas de l'écran.
 */
export function CheckoutFormV2(props: CheckoutFormProps) {
  const [confirmation, setConfirmation] = useState<CheckoutSuccess | null>(null);
  const [amountPaid, setAmountPaid] = useState(0);
  // Mise en page fournie par la nouvelle interface (layout.ts) ; sans fournisseur : rendu historique.
  const { AdvantagesFrame } = useContext(CheckoutLayoutContext);

  // Deux colonnes seulement si le conteneur (pas l'écran) est assez large :
  // le thème WordPress du site peut restreindre la zone de contenu.
  const [rootRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const wide = containerWidth === 0 ? window.innerWidth >= 1100 : containerWidth >= 900;
  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 768;

  const c = useCheckoutForm({
    ...props,
    onSuccess: (result) => {
      setConfirmation(result);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  // La carte enregistrée arrive avec /client/completeinfo, souvent APRÈS le
  // montage du formulaire (valeurs par défaut figées). Tant que le client n'a
  // pas choisi lui-même, on présélectionne sa carte enregistrée.
  const userChoseOption = useRef(false);
  useEffect(() => {
    if (userChoseOption.current) return;
    if (c.clientHasStoredCreditCard && !c.storedCardExpiresBeforeEngagement && c.paymentMethod === 'Credit Card') {
      c.form.setValue('StoredCreditCard', 'saved');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.clientHasStoredCreditCard, c.storedCardExpiresBeforeEngagement]);

  const {
    form,
    register,
    handleSubmit,
    onSubmit,
    product,
    productAmount,
    totalAmount,
    remainingAmount,
    vatAmount,
    discount,
    giftCardValue,
    giftCardValueCoversTotalAmount,
    clientCompleteInfo,
    clientCreditCard,
    clientHasStoredCreditCard,
    storedCardExpiresBeforeEngagement,
    storedCreditCard,
    paymentMethod,
    handlePaymentChange,
    customPaymentMethods,
    isAlmaAvailable,
    isAlmaSelectable,
    isApplePayAvailable,
    isGooglePayAvailable,
    walletStep,
    selectedPlan,
    updatedPaymentPlans,
    paymentInProgress,
    isSubmitting,
    almaRedirectInProgress,
    submitEnabled,
    disableConfirmAlmaPayment,
    getDisabledTooltipMessage,
    isContract,
    contractAgreement,
    isLoggedIn,
    navigate,
  } = c;

  if (!isLoggedIn) {
    const currentPath = location.pathname + location.search + location.hash;
    navigate('/account', { state: { onLoginRedirect: currentPath }, replace: true });
    return null;
  }

  if (!product) {
    return (
      <Box p={4} textAlign="center">
        <Text>Erreur : aucun produit sélectionné</Text>
      </Box>
    );
  }

  if (confirmation) {
    return (
      <Stack ref={rootRef} spacing={{ base: 8, md: 12 }} className="CheckoutV2 cv2 CheckoutV2--confirmed" style={cssVars} data-font={configuredFont || undefined}>
        <PageHeader current={3} title="Merci" wide={wide} />
        <Confirmation product={product} result={confirmation} amountPaid={amountPaid} isContract={isContract} />
      </Stack>
    );
  }

  // ── Option sélectionnée (dérivée de l'état du hook, jamais dupliquée) ──
  const selected: OptionId =
    paymentMethod === APPLE_PAY_METHOD
      ? 'applepay'
      : paymentMethod === GOOGLE_PAY_METHOD
        ? 'googlepay'
        : paymentMethod !== 'Credit Card'
          ? `custom:${paymentMethod}`
          : storedCreditCard === 'saved'
            ? 'saved'
            : 'new';

  const select = (id: string) => {
    userChoseOption.current = true;
    if (id === 'saved') {
      form.setValue('StoredCreditCard', 'saved');
      handlePaymentChange('Credit Card');
    } else if (id === 'new') {
      form.setValue('StoredCreditCard', 'new');
      handlePaymentChange('Credit Card');
    } else if (id === 'applepay') {
      handlePaymentChange(APPLE_PAY_METHOD);
    } else if (id === 'googlepay') {
      handlePaymentChange(GOOGLE_PAY_METHOD);
    } else if (id.startsWith('custom:')) {
      handlePaymentChange(id.slice('custom:'.length));
    }
  };

  const needsPayment = remainingAmount > 0 && !giftCardValueCoversTotalAmount;
  const needsBillingAddress = needsPayment && (selected === 'new' || selected.startsWith('custom:'));
  const isBusy = paymentInProgress || isSubmitting || almaRedirectInProgress;
  const disabledReason = getDisabledTooltipMessage();
  const shortDisabledReason = disabledReason.startsWith('Veuillez remplir') ? 'Complétez les champs obligatoires pour payer' : disabledReason;
  const isDisabled = (remainingAmount === 0 ? false : !submitEnabled || disableConfirmAlmaPayment) || isBusy;
  const storedCard = clientCompleteInfo?.Client.ClientCreditCard;
  const lastFour = storedCard?.LastFour || String(storedCard?.CardNumber ?? '').replace(/\D/g, '').slice(-4);

  const ctaLabel = isBusy
    ? paymentMethod === 'Alma'
      ? almaRedirectInProgress
        ? 'Redirection vers Alma…'
        : 'Préparation du paiement…'
      : isContract
        ? 'Souscription en cours…'
        : 'Traitement du paiement…'
    : remainingAmount === 0
      ? 'Confirmer'
      : paymentMethod === 'Alma'
        ? selectedPlan
          ? `Payer en ${selectedPlan}×`
          : 'Commencer le paiement'
        : WALLET_METHODS.includes(paymentMethod)
          ? `Payer avec ${paymentMethod}`
          : isContract
            ? "Souscrire à l'abonnement"
            : `Payer ${formatAmount(remainingAmount)}`;

  const submitButton = (full: boolean) => (
    <Tooltip label={disabledReason} isDisabled={!disabledReason || isBusy || isMobileViewport} hasArrow placement="top">
      {/* type="button" + requestSubmit() : les thèmes Uncode imposent
          `#mindbody-app button[type=submit] { background: black !important }`,
          impossible à battre proprement ; en n'étant pas un submit natif le
          bouton garde la couleur de marque. La touche Entrée soumet toujours
          le <form> via son onSubmit. */}
      <button
        type="button"
        className={`cv2-cta CheckoutV2Submit${full ? ' cv2-cta--full' : ''}${WALLET_METHODS.includes(paymentMethod) ? ' cv2-cta--dark' : ''}`}
        disabled={isDisabled}
        aria-busy={isBusy}
        onClick={() => {
          setAmountPaid(remainingAmount);
          const formEl = document.getElementById(FORM_ID) as HTMLFormElement | null;
          formEl?.requestSubmit();
        }}
      >
        {ctaLabel}
      </button>
    </Tooltip>
  );

  const summaryProps = {
    product,
    isContract,
    productAmount,
    discount,
    promoCode: form.watch('PromoCode'),
    giftCardValue,
    totalAmount,
    remainingAmount,
    vatAmount,
    paymentMethod,
    selectedPlan,
    paymentPlans: updatedPaymentPlans,
  };

  const isLululemon = [113276, 113277, 113278].includes(Number(product.Id));
  const legal = <LegalNote isContract={isContract} paymentMethod={paymentMethod} remainingAmount={remainingAmount} storedCreditCard={storedCreditCard} alignRight={!isMobileViewport} />;

  return (
    <FormProvider {...form}>
      <Stack ref={rootRef} spacing={{ base: 7, md: 10 }} className={`CheckoutV2 cv2${wide ? ' CheckoutV2--wide' : ''}`} style={cssVars} data-font={configuredFont || undefined}>
        <PageHeader current={2} title="Paiement" wide={wide} />

        <Grid templateColumns={wide ? 'minmax(0, 1fr) 380px' : 'minmax(0, 1fr)'} gap={wide ? 10 : 5} alignItems="start">
          {/* ── Colonne formulaire ── */}
          <Stack as="form" id={FORM_ID} onSubmit={handleSubmit(onSubmit)} spacing={5} minW={0} noValidate>
            {!wide && <OrderSummary {...summaryProps} compact />}

            {isContract && product.type === 'contract' && clientCreditCard?.ExpYear && clientCreditCard?.ExpMonth && (
              <SavedCardExpirationAlert card={{ ExpYear: clientCreditCard.ExpYear, ExpMonth: clientCreditCard.ExpMonth, LastFour: clientCreditCard.CardNumber }} contract={product} />
            )}

            {/* Nouvelle interface seulement (layout.ts) : avantages au-dessus du moyen de paiement. */}
            {AdvantagesFrame && product.type === 'service' && (
              <AdvantagesFrame product={product}>
                <Advantages product={product} />
              </AdvantagesFrame>
            )}

            {needsPayment ? (
              <Section index="01" title="Moyen de paiement" className="CheckoutV2Section--payment">
                <Stack spacing={3} role="radiogroup">
                  <WalletExpress
                    applePay={isApplePayAvailable}
                    googlePay={isGooglePayAvailable}
                    selected={selected === 'applepay' || selected === 'googlepay' ? selected : null}
                    onSelect={select}
                    ownGroup={false}
                  />

                  {clientHasStoredCreditCard && (
                    <PaymentOption
                      id="saved"
                      icon="saved"
                      selected={selected === 'saved'}
                      disabled={storedCardExpiresBeforeEngagement}
                      disabledReason="Cette carte expire avant la fin de l'engagement"
                      title={`Carte enregistrée •••• ${lastFour ?? ''}`}
                      subtitle={storedCard?.CardType ? `${storedCard.CardType} · paiement immédiat sécurisé` : 'Paiement immédiat sécurisé'}
                      onSelect={select}
                    />
                  )}

                  <PaymentOption
                    id="new"
                    icon="card"
                    selected={selected === 'new'}
                    title={clientHasStoredCreditCard ? 'Nouvelle carte bancaire' : 'Carte bancaire'}
                    subtitle="Paiement immédiat sécurisé"
                    trailing={<CardBrandsRow />}
                    onSelect={select}
                  >
                    <CardFields c={c} />
                  </PaymentOption>

                  {isAlmaAvailable &&
                    customPaymentMethods?.map((method) => (
                      <PaymentOption
                        key={method.Id}
                        id={`custom:${method.Name}`}
                        icon="alma"
                        selected={selected === `custom:${method.Name}`}
                        disabled={!isAlmaSelectable}
                        disabledReason="Disponible à partir de 100 € d'achat"
                        title="Payer avec Alma"
                        subtitle="Par carte bancaire"
                        onSelect={select}
                      >
                        <AlmaPlans c={c} />
                      </PaymentOption>
                    ))}
                </Stack>
              </Section>
            ) : (
              <Section index="01" title="Rien à régler" className="CheckoutV2Section--free">
                <Text className="cv2-body">Votre code promo ou votre carte cadeau couvre l'intégralité de la commande. Il ne vous reste qu'à confirmer.</Text>
              </Section>
            )}

            {needsBillingAddress && (
              <Section index="02" title="Adresse de facturation" hint={selected.startsWith('custom:') ? "Requise par Alma pour l'échéancier." : undefined}>
                <Stack spacing={4}>
                  {/* BillingName est aussi le titulaire transmis à Mindbody pour une
                      carte : un seul champ, ici, pour ne pas le demander deux fois. */}
                  <Field label="Nom de facturation" isRequired={selected === 'new'}>
                    <Input
                      className="cv2-input"
                      variant="unstyled"
                      autoComplete="name"
                      placeholder={selected === 'new' ? 'Tel qu\'inscrit sur la carte' : 'Prénom et nom'}
                      {...register('BillingName', { required: remainingAmount > 0 && selected === 'new' })}
                    />
                  </Field>
                  {/* address-line1 (et non street-address) : sur Safari, street-address
                      remplit une adresse complète, code postal et ville compris. */}
                  <Field label="Adresse" isRequired>
                    <Input className="cv2-input" variant="unstyled" autoComplete="address-line1" placeholder="Numéro et rue" {...register('BillingAddress')} />
                  </Field>
                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
                    <Field label="Ville" isRequired flex={2}>
                      <Input className="cv2-input" variant="unstyled" autoComplete="address-level2" placeholder="Ville" {...register('BillingCity')} />
                    </Field>
                    <Field label="Code postal" isRequired flex={1} minW="140px">
                      <Input className="cv2-input" variant="unstyled" autoComplete="postal-code" inputMode="numeric" placeholder="75000" {...register('BillingPostalCode')} />
                    </Field>
                  </Stack>
                </Stack>
              </Section>
            )}

            {!AdvantagesFrame && product.type === 'service' && (
              <Section index={needsBillingAddress ? '03' : '02'} title="Avantages" hint="Un code promo ou une carte cadeau ? C'est ici.">
                <Advantages product={product} />
              </Section>
            )}

            {isContract && (
              <Box className="cv2-section">
                <ContractAgreementCheckbox pdfUrl={contractAgreement?.pdfUrl} agreementTerms={contractAgreement?.agreementTerms} />
              </Box>
            )}

            {isLululemon && (
              <Text className="cv2-small" px={1}>
                En achetant ce pack, vous acceptez de recevoir des e-mails promotionnels de la part de notre partenaire lululemon sur les produits, services, offres, histoires et événements conformément à la{' '}
                <a href="https://www.lululemon.fr/fr-fr/legal/politique-de-confidentialite/privacy.html" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                  politique de confidentialité
                </a>{' '}
                de lululemon (vous pouvez vous désabonner via lululemon à tout moment).
              </Text>
            )}

            {/* ── Bouton (hors mobile) ── */}
            {!isMobileViewport && (
              <Stack spacing={3} pt={2} align="flex-end">
                {submitButton(false)}
                {disabledReason && !isBusy && (
                  <Text className="cv2-small" textAlign="right" maxW="520px">
                    {disabledReason}
                  </Text>
                )}
                {legal}
              </Stack>
            )}
            {isMobileViewport && <Box>{legal}</Box>}
          </Stack>

          {/* ── Colonne récapitulatif (conteneur large) ── */}
          {wide && (
            <Box position="sticky" top="24px">
              <OrderSummary {...summaryProps} />
            </Box>
          )}
        </Grid>

        {/* ── Barre fixe mobile ── */}
        {isMobileViewport && (
          <Box className="cv2-stickybar CheckoutV2StickyBar">
            <Box className="cv2-stickybar-total">
              <Text className="cv2-stickybar-label">Total</Text>
              <Text className="cv2-stickybar-amount cv2-num">{formatAmount(remainingAmount)}</Text>
            </Box>
            {submitButton(true)}
            {shortDisabledReason && !isBusy && <Text className="cv2-stickybar-hint">{shortDisabledReason}</Text>}
          </Box>
        )}
      </Stack>

      {/* Départ vers la page de paiement du portefeuille : le client ne doit ni
          cliquer ailleurs ni croire que rien ne se passe. */}
      <WalletPaymentOverlay isOpen={walletStep !== 'idle'} currentStep={walletStep} walletName={paymentMethod} />
    </FormProvider>
  );
}

function LegalNote({ isContract, paymentMethod, remainingAmount, storedCreditCard, alignRight }: { isContract: boolean; paymentMethod: string; remainingAmount: number; storedCreditCard: string; alignRight: boolean }) {
  const sca = paymentMethod === 'Credit Card' && remainingAmount >= 30;
  return (
    <Stack spacing={1.5} textAlign={alignRight ? 'right' : 'center'} maxW="560px" alignSelf={alignRight ? 'flex-end' : 'center'}>
      {sca && (
        <Text className="cv2-small" display="inline-flex" alignItems="center" justifyContent={alignRight ? 'flex-end' : 'center'} gap="6px">
          <LockIcon boxSize="10px" />
          <span>{storedCreditCard === 'new' ? 'Authentification 3D Secure par votre banque, puis retour automatique ici.' : 'Votre banque peut demander une authentification 3D Secure.'}</span>
        </Text>
      )}
      <Text className="cv2-small">
        {isContract
          ? "En validant, vous acceptez les conditions de l'abonnement. Votre abonnement peut apparaître dans Mon compte d'ici une minute."
          : "En validant, vous acceptez les conditions générales de vente. Le crédit acheté peut mettre quelques instants avant de s'afficher dans votre compte."}
      </Text>
    </Stack>
  );
}
